import { WarningIcon } from "@phosphor-icons/react/dist/ssr/Warning";
import { motion } from "motion/react";
import { useMemo, useReducer, useRef } from "react";

import { logError, useTheme } from "@up/data";
import {
  ELEMENT_WRAPPER_MB,
  ElementWrapper,
  ErrorMessage,
  Form,
  InputText,
  Label,
  createValidation,
  getErrorMessage,
} from "@up/form";
import { Check, MOTION, Spinner } from "@up/ui";

import { saveGuardian } from "./network";

import type { TCart } from "@up/data";
import type { DataTypes, TErrorMessageKey } from "@up/form";
import type { FieldErrors, UseFormRegister } from "react-hook-form";

type Status = "idle" | "saving" | "saved" | "error";

type StatusType = {
  status: Status;
  errorMessage: string;
};

type ActionType =
  | { type: "idle" }
  | { type: "saving" }
  | { type: "saved" }
  | { type: "error"; payload: string };

type FormElementProps = {
  formErrors: FieldErrors;
  formValues: DataTypes.FormValues;
  register: UseFormRegister<any>;
};

const GuardianFirstName = function ({
  formErrors,
  formValues,
  register,
}: FormElementProps) {
  const KEY = "firstName";
  const VALIDATION = { presence: true };
  const ERRORS = formErrors[KEY];

  return (
    <ElementWrapper className={ELEMENT_WRAPPER_MB} hasErrors={!!ERRORS}>
      <Label htmlFor={KEY} required={true} text="Guardian First Name" />
      <InputText
        registerProps={register(KEY, createValidation(VALIDATION, formValues))}
      />
      <ErrorMessage
        text={
          ERRORS
            ? getErrorMessage(ERRORS.type as TErrorMessageKey, VALIDATION)
            : null
        }
      />
    </ElementWrapper>
  );
};

const GuardianLastName = function ({
  formErrors,
  formValues,
  register,
}: FormElementProps) {
  const KEY = "lastName";
  const VALIDATION = { presence: true };
  const ERRORS = formErrors[KEY];

  return (
    <ElementWrapper className={ELEMENT_WRAPPER_MB} hasErrors={!!ERRORS}>
      <Label htmlFor={KEY} required={true} text="Guardian Last Name" />
      <InputText
        registerProps={register(KEY, createValidation(VALIDATION, formValues))}
      />
      <ErrorMessage
        text={
          ERRORS
            ? getErrorMessage(ERRORS.type as TErrorMessageKey, VALIDATION)
            : null
        }
      />
    </ElementWrapper>
  );
};

const GuardianEmail = function ({
  formErrors,
  formValues,
  register,
}: FormElementProps) {
  const KEY = "email";
  const VALIDATION = { email: true, presence: true };
  const ERRORS = formErrors[KEY];

  return (
    <ElementWrapper className={ELEMENT_WRAPPER_MB} hasErrors={!!ERRORS}>
      <Label htmlFor={KEY} required text="Guardian Email" />
      <InputText
        registerProps={register(KEY, createValidation(VALIDATION, formValues))}
      />
      <ErrorMessage
        text={
          ERRORS
            ? getErrorMessage(ERRORS.type as TErrorMessageKey, VALIDATION)
            : null
        }
      />
    </ElementWrapper>
  );
};

type Props = {
  showGuardianEmail: boolean;
  cancelFn: () => void;
  guardian?: TCart["guardian"];
  saveSuccess: (cart: TCart) => void;
};

export default function GuardianInformationForm({
  showGuardianEmail,
  cancelFn,
  guardian,
  saveSuccess,
}: Props) {
  const theme = useTheme();
  const savedCartRef = useRef<TCart | null>(null);

  const [save, saveDispatch] = useReducer(
    function (
      state: { status: Status; errorMessage: string },
      action: ActionType,
    ): StatusType {
      const nextState = { ...state };

      switch (action.type) {
        case "saving":
          return {
            ...nextState,
            status: "saving",
          };
        case "saved":
          return {
            ...nextState,
            status: "saved",
          };
        case "error":
          return {
            ...nextState,
            status: "error",
            errorMessage: action.payload,
          };
        default:
          return nextState;
      }
    },
    {
      status: "idle",
      errorMessage: "",
    },
  );

  const onSubmit = async function (data: Record<string, unknown>) {
    const g = data as TCart["guardian"];

    saveDispatch({ type: "saving" });
    try {
      const cart = await saveGuardian(g);
      savedCartRef.current = cart;
      saveDispatch({ type: "saved" });
    } catch (err) {
      saveDispatch({ type: "error", payload: "save error" });
      logError({ action: "GUARDIAN_INFO_SAVE", error: err });
    }
  };

  const userData = useMemo(() => {
    return {
      firstName: guardian?.firstName ?? "",
      lastName: guardian?.lastName ?? "",
      email: guardian?.email ?? "",
    };
  }, [guardian]);

  const IS_SAVING = save.status === "saving";
  const DID_SAVE = save.status === "saved";
  const DID_ERROR = save.status === "error";

  return (
    <motion.div className="overflow-hidden" {...MOTION["expand"].props}>
      <div className="pt-2" data-testid="guardian-form">
        <Form onSubmit={onSubmit} userData={userData} validateOnMount={true}>
          {({ control, errors, formValues, register }) => {
            return (
              <>
                <div className="md:grid gap-4 md:grid-cols-2">
                  <GuardianFirstName
                    formErrors={errors}
                    formValues={formValues}
                    register={register}
                  />
                  <GuardianLastName
                    formErrors={errors}
                    formValues={formValues}
                    register={register}
                  />
                </div>

                {showGuardianEmail ? (
                  <div className="md:grid grid-cols-2 gap-4">
                    <GuardianEmail
                      formErrors={errors}
                      formValues={formValues}
                      register={register}
                    />
                  </div>
                ) : (
                  <input type="hidden" {...register("email")} />
                )}

                {DID_ERROR ? (
                  <div className="mb-3 flex items-center font-bold text-red-700">
                    <WarningIcon className="mr-2" size={24} weight="fill" />
                    We had trouble saving your changes. Please try again.
                  </div>
                ) : null}

                <div className="flex items-center">
                  <input
                    className={`${theme.buttonPrimaryClass} mr-2 ${
                      IS_SAVING ? "opacity-75" : ""
                    }`}
                    disabled={IS_SAVING}
                    type="submit"
                    value="Save Changes"
                  />
                  <input
                    className={`${theme.buttonSecondaryClass} ${
                      IS_SAVING ? "opacity-75" : ""
                    }`}
                    disabled={IS_SAVING}
                    onClick={cancelFn}
                    type="button"
                    value="Cancel"
                  />
                  {IS_SAVING ? <Spinner className="ml-4" /> : null}
                  {DID_SAVE ? (
                    <div className="ml-2">
                      <Check
                        animationCallback={() => {
                          if (savedCartRef.current) {
                            saveSuccess(savedCartRef.current);
                          }
                        }}
                        size={44}
                      />
                    </div>
                  ) : null}
                </div>
              </>
            );
          }}
        </Form>
      </div>
    </motion.div>
  );
}
